/**
 * Color picker row（表面 / 内側）。
 *
 * 設計書 FR-03 / FR-04 / FR-05 / 10.2。プロトタイプの `.color-swatch`
 * マークアップに一致させ、`<input type="color">` と HEX 表示を横に並べる。
 *
 * `input` イベントを用いる理由:
 * プロトタイプ同様、カラーピッカーのドラッグ中にリアルタイム反映する
 * ため。`change` イベントだと picker を閉じるまで発火しない。
 */

import type { AppState, Store } from '../../state';

/**
 * 表面 / 内側 / 外周テキスト / 背景色のいずれをバインドするかを指定する識別子。
 *
 * `'textColor'` は spec §6 / FR-25 / D-20 で独立ピッカとして扱うフィールド。
 * `'backgroundColor'` は Phase C 修正ラウンドで追加された背景色フィールド
 * （AppState.backgroundColor、default `#0f0f0f`）。`backgroundTransparent`
 * が `true` のときは DOM レベルで `<input type="color">` を disabled にし、
 * store の値は保持する（mountColorPicker 内で自己完結して subscribe する）。
 */
export type ColorTarget = 'surfaceColor' | 'innerColor' | 'textColor' | 'backgroundColor';

/**
 * `ColorTarget` に対応するラベル（日本語 + 英語）。
 * 設計書 10.4「日本語 + 英語併記」に従う。
 */
const LABELS: Readonly<Record<ColorTarget, string>> = {
  surfaceColor: '表面 Surface',
  innerColor: '内側 Inner',
  textColor: 'テキスト Text',
  backgroundColor: '背景 Background',
};

/**
 * `mountColorPicker` のオプション。
 */
export interface ColorPickerOptions {
  /**
   * 鍵アイコン（arare-panel の lock affordance）を行末に置くか。
   * `true` で `AppState.colorLocks[target]` にバインドされたロックトグルを
   * 表示する。ロックは RANDOMIZE COLORS の上書きだけを防ぎ、手動編集は
   * ロック中も常に許可される（input は disabled にしない）。
   */
  lockable?: boolean;

  /**
   * プリセットスウォッチ。指定すると hex 読み値の左に並び、クリックで
   * その色を `target` に書き込む。手動選択の一種なので、ロック中でも
   * 常に有効（鍵が縛るのはランダマイズの上書きのみ）。
   *
   * 各要素は `{ hex, name }`。`name` は aria-label / title 用の呼称。
   */
  presets?: readonly ColorPreset[];
}

/** プリセットスウォッチ 1 個分の定義。 */
export interface ColorPreset {
  /** 書き込む HEX 文字列（`#rrggbb`）。 */
  hex: string;
  /** aria-label / title 用の色名（例: `"赤 Red"`）。 */
  name: string;
}

/**
 * 鍵アイコンの SVG（monotone / currentColor / 1px stroke、絵文字不使用）。
 * arare-panel の規約: 開いた掛け金 = アンロック、閉じた掛け金 = ロック。
 */
const LOCK_OPEN_SVG =
  '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" aria-hidden="true">' +
  '<rect x="2.5" y="5.5" width="7" height="5"/><path d="M8 5.5V4a2 2 0 0 0-4 0"/></svg>';
const LOCK_CLOSED_SVG =
  '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" aria-hidden="true">' +
  '<rect x="2.5" y="5.5" width="7" height="5"/><path d="M4 5.5V4a2 2 0 0 1 4 0v1.5"/></svg>';

/**
 * ユーザ入力の 16 進カラー文字列を `#rrggbb`（小文字）へ正規化する。
 *
 * 許容形式: `#rrggbb` / `rrggbb` / `#rgb` / `rgb`（大文字小文字不問、
 * 前後空白は無視）。それ以外は `null`（呼び出し側で revert）。
 */
function normalizeHex(raw: string): string | null {
  const t = raw.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{6}$/.test(t)) return `#${t}`;
  if (/^[0-9a-f]{3}$/.test(t)) {
    return `#${t[0]}${t[0]}${t[1]}${t[1]}${t[2]}${t[2]}`;
  }
  return null;
}

/**
 * 表面 / 内側のカラーピッカー行を 1 つマウントする。
 *
 * Colors セクションには本関数を 2 回呼んで並べる:
 * `mountColorPicker(root, store, 'surfaceColor')`
 * `mountColorPicker(root, store, 'innerColor')`
 *
 * @param root - Colors セクションのコンテナ
 * @param store - グローバルストア
 * @param target - `'surfaceColor'` または `'innerColor'`
 * @param options - `lockable: true` で鍵アイコンを表示（既定 false）
 * @returns teardown 関数
 */
export function mountColorPicker(
  root: HTMLElement,
  store: Store<AppState>,
  target: ColorTarget,
  options: ColorPickerOptions = {},
): () => void {
  const row = document.createElement('div');
  row.className = 'row';

  const label = document.createElement('label');
  label.textContent = LABELS[target];
  row.appendChild(label);

  const swatch = document.createElement('div');
  swatch.className = 'color-swatch';

  // hex 読み値は直接編集可能な text input（arare-panel の editable value
  // パターン、2026-07-10）。見た目は従来の読み値と同一で、focus 時のみ
  // ink ヘアライン下線が出る（app.css .hex-input）。Enter / blur で commit、
  // Esc で revert。入力中は store に書かない。
  const hex = document.createElement('input');
  hex.type = 'text';
  hex.className = 'hex hex-input';
  hex.spellcheck = false;
  hex.autocomplete = 'off';
  hex.setAttribute('aria-label', `${LABELS[target]} HEX`);

  const input = document.createElement('input');
  input.type = 'color';

  // 初期値を state から取得
  const initial = store.get()[target];
  input.value = initial;
  hex.value = initial.toUpperCase();

  // ドラッグ中もリアルタイム反映したいので input イベントを使う
  input.addEventListener('input', (e) => {
    const value = (e.target as HTMLInputElement).value;
    store.set({ [target]: value } as Partial<AppState>);
  });

  /** hex input の表示を store の現値へ戻す（revert / commit 後の正規化表示）。 */
  const restoreHex = (): void => {
    hex.value = store.get()[target].toUpperCase();
  };
  /** 入力値を検証して store へ commit。不正なら revert。 */
  const commitHex = (): void => {
    const normalized = normalizeHex(hex.value);
    if (normalized === null) {
      restoreHex();
      return;
    }
    store.set({ [target]: normalized } as Partial<AppState>);
    // 3 桁形式等も正規化後の 6 桁表示に揃える（store 変更が同値で
    // 購読が発火しないケースがあるため明示的に書き戻す）。
    restoreHex();
  };
  hex.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      commitHex();
      hex.blur();
    } else if (e.key === 'Escape') {
      restoreHex();
      hex.blur();
    }
  });
  hex.addEventListener('blur', commitHex);
  hex.addEventListener('focus', () => hex.select());

  // プリセットスウォッチ（arare-panel 作法: sharp / 1px hairline 枠 /
  // hover で ink 枠のみ）。クリック = 手動選択なのでロックの影響を受けない。
  if (options.presets !== undefined && options.presets.length > 0) {
    const presets = document.createElement('div');
    presets.className = 'color-presets';
    for (const preset of options.presets) {
      const presetBtn = document.createElement('button');
      presetBtn.type = 'button';
      presetBtn.className = 'color-preset';
      presetBtn.style.backgroundColor = preset.hex;
      presetBtn.setAttribute('aria-label', `${LABELS[target]}: ${preset.name} ${preset.hex}`);
      presetBtn.title = `${preset.name} ${preset.hex}`;
      presetBtn.addEventListener('click', () => {
        store.set({ [target]: preset.hex } as Partial<AppState>);
      });
      presets.appendChild(presetBtn);
    }
    swatch.appendChild(presets);
  }

  swatch.appendChild(hex);
  swatch.appendChild(input);

  // 鍵アイコン（arare-panel lock affordance）。行末（swatch の右端）に置く。
  // ロック中は hex 読み値を CSS 側（.row.locked .hex）で dim するが、
  // input 自体は生かしたままにする — 鍵が縛るのはランダマイズのみ。
  let syncLock: (() => void) | null = null;
  if (options.lockable) {
    const lockBtn = document.createElement('button');
    lockBtn.type = 'button';
    lockBtn.className = 'color-lock';
    lockBtn.setAttribute('aria-label', `Lock ${LABELS[target]}`);

    syncLock = (): void => {
      const locked = store.get().colorLocks[target];
      lockBtn.innerHTML = locked ? LOCK_CLOSED_SVG : LOCK_OPEN_SVG;
      lockBtn.setAttribute('aria-pressed', String(locked));
      lockBtn.classList.toggle('locked', locked);
      row.classList.toggle('locked', locked);
    };
    syncLock();

    lockBtn.addEventListener('click', () => {
      const locks = store.get().colorLocks;
      store.set({ colorLocks: { ...locks, [target]: !locks[target] } });
    });
    swatch.appendChild(lockBtn);
  }

  row.appendChild(swatch);
  root.appendChild(row);

  // Background 専用: `backgroundTransparent` ON で input を disabled に。
  // store の `backgroundColor` 値は保持したまま、DOM 上の編集だけを封じる。
  // 他の target（surface / inner / text）では購読自体を張らず、ノーコスト。
  const syncDisabled = (): void => {
    if (target !== 'backgroundColor') return;
    const disabled = store.get().backgroundTransparent;
    input.disabled = disabled;
    // hex 直接入力も color picker と同じく封じる（Transparent 中の
    // backgroundColor は描画に使われず、編集を許すと混乱するため）。
    hex.disabled = disabled;
  };
  syncDisabled();

  // 外部からの変更（プリセット読み込み / RANDOMIZE COLORS 等）に追従。
  //
  // Phase F Step 1 bug 修正（2026-04-21）:
  // 旧実装は `input.value !== next` guard で programmatic 更新時の無駄な DOM
  // 書き込みを避けていたが、RANDOMIZE COLORS ボタンで Surface / Inner の
  // input と hex が更新されない症状が報告された。`<input type="color">` の
  // value 書き戻し時に稀に同値判定が誤検出されるブラウザ quirk を疑い、
  // guard を撤去して無条件更新に変更。ユーザ入力由来のフィードバックループは
  // ドラッグ中でも input.value の往復書き込みはコストゼロ相当で問題ない。
  const unsubscribe = store.subscribe((state, prev) => {
    if (state[target] !== prev[target]) {
      const next = state[target];
      input.value = next;
      // focus ガード（arare-panel の editable value 規約）: 入力中の
      // フィールドへは書き戻さない。commit / blur 時に restoreHex が揃える。
      if (document.activeElement !== hex) {
        hex.value = next.toUpperCase();
      }
    }
    if (
      target === 'backgroundColor' &&
      state.backgroundTransparent !== prev.backgroundTransparent
    ) {
      syncDisabled();
    }
    // 外部からのロック変更（将来のプリセット読込等）にも追従する。
    if (syncLock !== null && state.colorLocks[target] !== prev.colorLocks[target]) {
      syncLock();
    }
  });

  return (): void => {
    unsubscribe();
    row.remove();
  };
}
